// Watches the producer's own location and keeps a blue-dot marker on the
// given map in sync with it — the single place that wires geolocation,
// the marker ref, and the create/move effect together. Both the Live
// Tracking view and the zone setup map get the dot from one call.
// Returns the location too, for callers that also need the value (e.g. a
// "center on me" button or a distance readout). `ready` gates drawing
// until the map's style has loaded — each caller passes its own readiness
// signal (mapLoaded / mapInitialized).

import { useEffect, useRef, type MutableRefObject } from "react";
import type mapboxgl from "mapbox-gl";
import { upsertProducerMarker, type LngLat } from "@/lib/producerLocationMarker";
import { useProducerLocation } from "@/hooks/useProducerLocation";

export const useProducerLocationMarker = (
  mapRef: MutableRefObject<mapboxgl.Map | null>,
  ready: boolean,
): LngLat | null => {
  const producerLocation = useProducerLocation();
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!ready || !mapRef.current || !producerLocation) return;
    markerRef.current = upsertProducerMarker(mapRef.current, markerRef.current, producerLocation);
  }, [producerLocation, ready, mapRef]);

  return producerLocation;
};
